import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  service: string;
  message: string;
  poolSize: string;
  "bot-field": string;
}

const QuoteForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: '',
    poolSize: '',
    "bot-field": '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.poolSize) newErrors.poolSize = 'Please select your pool size';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const encode = (data: { [key: string]: string }) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // If bot field is filled, silently reject
    if (formData["bot-field"]) {
      setIsSubmitted(true);
      return;
    }
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        const response = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: encode({
            "form-name": "quote-request",
            ...formData
          })
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          message: '',
          poolSize: '',
          "bot-field": '',
        });
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('There was an error submitting the form. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <CheckCircle className="h-16 w-16 text-green-500" />
        </div>
        <h3 className="text-2xl font-bold text-purple-900 mb-2">Thank You!</h3>
        <p className="text-gray-700 mb-4">
          Your quote request has been submitted successfully. We'll review your request and get back to you soon.
        </p>
        <p className="text-orange-600 font-semibold mb-4">
          Remember, as a new customer you'll receive your FIRST MONTH FREE!
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="px-6 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 transition-colors duration-200"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form 
      name="quote-request"
      method="POST"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={handleSubmit}
      className="bg-white rounded-lg shadow-xl p-6 md:p-8"
    >
      <input type="hidden" name="form-name" value="quote-request" />
      
      <p className="hidden">
        <label>
          Don't fill this out if you're human: 
          <input
            name="bot-field"
            value={formData["bot-field"]}
            onChange={handleChange}
          />
        </label>
      </p>
      
      <h3 className="text-2xl font-bold text-purple-900 mb-6">Get Your Free Quote</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="name" className="block text-gray-700 font-medium mb-1">Full Name*</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.name && <p className="mt-1 text-red-500 text-sm">{errors.name}</p>}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email Address*</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.email && <p className="mt-1 text-red-500 text-sm">{errors.email}</p>}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">Phone Number*</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.phone && <p className="mt-1 text-red-500 text-sm">{errors.phone}</p>}
        </div>
        
        <div>
          <label htmlFor="address" className="block text-gray-700 font-medium mb-1">Address*</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.address && <p className="mt-1 text-red-500 text-sm">{errors.address}</p>}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="service" className="block text-gray-700 font-medium mb-1">Service Needed*</label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.service ? 'border-red-500' : 'border-gray-300'}`}
          >
            <option value="">Select a service</option>
            <option value="weekly">Weekly Cleaning</option>
            <option value="biweekly">Bi-Weekly Cleaning</option>
            <option value="repairs">Pool Repairs</option>
            <option value="green">Green to Clean</option>
            <option value="rental">Short Term Rental Service</option>
          </select>
          {errors.service && <p className="mt-1 text-red-500 text-sm">{errors.service}</p>}
        </div>
        
        <div>
          <label htmlFor="poolSize" className="block text-gray-700 font-medium mb-1">Pool Size*</label>
          <select
            id="poolSize"
            name="poolSize"
            value={formData.poolSize}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.poolSize ? 'border-red-500' : 'border-gray-300'}`}
          >
            <option value="">Select pool size</option>
            <option value="small">Small (up to 10,000 gallons)</option>
            <option value="medium">Medium (10,000-20,000 gallons)</option>
            <option value="large">Large (20,000-40,000 gallons)</option>
            <option value="xlarge">Extra Large (40,000+ gallons)</option>
          </select>
          {errors.poolSize && <p className="mt-1 text-red-500 text-sm">{errors.poolSize}</p>}
        </div>
      </div>
      
      <div className="mb-4">
        <label htmlFor="message" className="block text-gray-700 font-medium mb-1">Additional Information</label>
        <textarea
          id="message"
          name="message"
          rows={4}
          value={formData.message}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder="Tell us about your pool or specific needs..."
        ></textarea>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
        <p className="text-purple-900 font-semibold">
          <span className="text-orange-600">Special Promotion:</span> First month of service FREE for new customers!
        </p>
      </div>
      
      <button
        type="submit"
        className="w-full py-3 bg-orange-500 text-white font-bold rounded-md hover:bg-orange-600 transition-colors duration-200 flex justify-center items-center"
        disabled={isLoading}
      >
        {isLoading ? (
          <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
        ) : null}
        {isLoading ? 'Submitting...' : 'Submit Quote Request'}
      </button>
    </form>
  );
};

export default QuoteForm;